import { Button, Checkbox, Drawer, FormControlLabel, Popover, Radio, RadioGroup, Stack, Box } from '@mui/material';
import { useIsMobileLayout } from '../../../common/hooks/useIsMobileLayout';
import { theme } from '../../../common/styles/theme';
import React from 'react';
import { Text } from '../../../common/components/Text/Text';
import { IConfirmPopupProps } from './types';
import { CLASS_DONT_CLOSE_TEXT_SELECTION } from '../../../common/constants';

export const ConfirmPopup: React.FC<IConfirmPopupProps> = ({
    anchor,
    text,
    lines,
    description,
    confirmTitle,
    closeTitle,
    options,
    defaultOption,
    checkboxLabel,
    defaultChecked,
    popoverProps,
    onCancel,
    onConfirm,
    onClose
}) => {
    const textList = text ? [text] : (lines || []);
    const isMobileLayout = useIsMobileLayout();
    const buttonSx = { height: theme.spacing(isMobileLayout ? 5 : 4), flex: isMobileLayout ? '1 1 0' : undefined };

    const [option, setOption] = React.useState(defaultOption);
    const [checked, setChecked] = React.useState(!!defaultChecked);

    // Reopening (anchor going from null to an element) starts a fresh choice each time.
    React.useEffect(() => {
        if (anchor) {
            setOption(defaultOption);
            setChecked(!!defaultChecked);
        }
    }, [anchor, defaultOption, defaultChecked]);

    const onCancelHandler = () => {
        if (onCancel) onCancel();
        onClose();
    };

    const onConfirmHandler = () => {
        onConfirm({ option, checked });
    };

    const content = (
        <Stack
            gap={theme.spacing(1)}
            sx={{
                p: theme.spacing(2),
            }}
        >
            <Box>
                {textList.map((text, index) => {
                    return <Text fontWeight={600} key={index}>{text}</Text>
                })}
                {!!description &&
                    <Text variant={'subtitle1'} sx={{ color: theme.palette.grey[500] }}>
                        {description}
                    </Text>
                }
            </Box>

            {!!options?.length &&
                <RadioGroup
                    value={option}
                    onChange={(e) => setOption(e.target.value)}
                >
                    {options.map((o) => (
                        <FormControlLabel
                            key={o.value}
                            value={o.value}
                            control={<Radio size={'small'} />}
                            sx={{ alignItems: 'flex-start', m: 0 }}
                            label={
                                <Stack sx={{ mt: theme.spacing(.25) }}>
                                    <Text variant={'subtitle2'}>{o.label}</Text>
                                    {!!o.hint &&
                                        <Text variant={'subtitle1'} sx={{ color: theme.palette.grey[500] }}>
                                            {o.hint}
                                        </Text>
                                    }
                                </Stack>
                            }
                        />
                    ))}
                </RadioGroup>
            }

            {!!checkboxLabel &&
                <FormControlLabel
                    control={
                        <Checkbox
                            size={'small'}
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                        />
                    }
                    label={<Text variant={'subtitle2'}>{checkboxLabel}</Text>}
                    sx={{ m: 0 }}
                />
            }

            <Stack
                direction="row"
                gap={theme.spacing()}
                columnGap={theme.spacing()}
                sx={{
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    onClick={onCancelHandler}
                    variant={'contained'}
                    color={'info'}
                    sx={buttonSx}
                >
                    {closeTitle || 'No'}
                </Button>
                <Button
                    onClick={onConfirmHandler}
                    variant={'contained'}
                    color={'primary'}
                    sx={buttonSx}
                >
                    {confirmTitle || 'Yes'}
                </Button>
            </Stack>
        </Stack>
    );

    // mobile: same content, but a bottom sheet instead of a popover at the anchor
    if (isMobileLayout) {
        return (
            <Drawer
                className={CLASS_DONT_CLOSE_TEXT_SELECTION}
                anchor={'bottom'}
                open={!!anchor}
                onClose={onClose}
                sx={{
                    zIndex: theme.zIndex.modal + 20, // above an open ButtonMenu sheet
                    '& > .MuiPaper-root': {
                        borderRadius: theme.spacing(2, 2, 0, 0),
                    },
                }}
            >
                {content}
            </Drawer>
        );
    }

    return (
        <Popover
            className={`
                ${CLASS_DONT_CLOSE_TEXT_SELECTION}
            `}
            {...popoverProps}
            open={!!anchor}
            anchorEl={anchor}
            anchorOrigin={popoverProps?.anchorOrigin || {
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={popoverProps?.transformOrigin || {
                vertical: 'top',
                horizontal: 'center',
            }}
            sx={{
                '& > .MuiPaper-root': {
                    borderRadius: theme.spacing(.75),
                },
            }}
            onClose={onClose}
        >
            {content}
        </Popover>
    );
}
